/**
 * Student theme page: lists the published chapters of a theme.
 * Route: /portal/courses/:track/:themeSlug
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, FileText } from "@phosphor-icons/react";
import { coursesApi } from "../../api/courses";
import { getTrackLabel } from "../../lib/tracks";

export default function PortalTheme() {
  const { t } = useTranslation();
  const { track, themeSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await coursesApi.theme(track, themeSlug);
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [track, themeSlug]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !data) {
    return <p className="text-sm text-red-600">{t("portal.courses.not_found")}</p>;
  }
  const { theme, chapters } = data;

  return (
    <div data-testid="portal-theme-page" className="space-y-8">
      <Link
        to={`/portal/courses?track=${track}`}
        data-testid="portal-theme-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {getTrackLabel(track, t)}
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("portal.courses.theme_kicker")}
        </p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          {theme.title}
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          {chapters.length} {t("portal.courses.chapters")}
        </p>
      </header>

      <div className="space-y-3">
        {chapters.length === 0 ? (
          <p className="text-sm text-slate-500">{t("portal.courses.no_chapter_yet")}</p>
        ) : (
          chapters.map((ch, i) => (
            <Link
              key={ch.id}
              to={`/portal/courses/${track}/${themeSlug}/${ch.slug}`}
              data-testid={`portal-chapter-${ch.slug}`}
              className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-slate-900 transition-colors"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                <FileText size={18} weight="regular" />
              </span>
              <span className="flex-1 text-sm font-medium text-slate-900">
                {String(i + 1).padStart(2, "0")} · {ch.title}
              </span>
              <ArrowRight
                size={16}
                weight="bold"
                className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
              />
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
